'use client';

import { useEffect, useRef } from 'react';

import { OrderUI } from '@libs/common-node';

import { useDecisions } from '../hooks/useDecisions';
import { useLoyaltyContext } from '../hooks/useLoyaltyContext';
import { usePageLayout } from '../hooks/usePageLayout';
import { useConsentCategory } from '../scripts/hooks/useConsentCategory';
import { buildExtras, buildPetsmartMedalliaContextFromOrder } from './buildPetsmartMedalliaContext';
import { useGetActiveOptimizelyCampaigns } from './useGetActiveOptimizelyCampaigns';

interface MedalliaTriggerProps {
    purchase: OrderUI;
}

export function MedalliaTrigger({ purchase }: MedalliaTriggerProps) {
    const { memberPoints } = useLoyaltyContext();
    const { user, userData } = usePageLayout();
    const hasC0002 = useConsentCategory('C0002');
    const ffMedalliaCustom = useDecisions('medallia_custom');

    const { orderNumber } = purchase;

    // Tracks the last serialized context written to window (null = never published for this order).
    const lastPublishedRef = useRef<string | null>(null);

    // Tracks the orderNumber for which the survey-trigger effect last called updatePageView.
    // The context effect uses this to detect when consent/flag becomes enabled *after* the
    // survey effect has already fired — in that case the context effect must notify the SDK.
    const surveyFiredForOrderRef = useRef<string | null>(null);

    // Captures a stable timestamp per orderNumber. Passed to the context builder so that
    // orderDate does not change on re-renders caused by enrichment (loyalty/profile data
    // arriving late), which would make every serialized context look different and defeat
    // the deduplication check below.
    const stableOrderDateRef = useRef<{ orderNumber: string; date: string } | null>(null);

    const optimizelyCampaigns = useGetActiveOptimizelyCampaigns(hasC0002);

    // Context population is gated on consent + flag; kept separate so it doesn't affect surveys.
    // Declared FIRST so window.petsmartMedalliaContext is populated before the survey-trigger
    // effect below calls updatePageView — the SDK reads custom parameters at that point.
    // `purchase` omitted from deps: enrichment only adds `webStyleId` (GTM), not Medallia fields.
    useEffect(() => {
        if (hasC0002 && ffMedalliaCustom && orderNumber) {
            const orderKey = String(orderNumber);

            if (stableOrderDateRef.current?.orderNumber !== orderKey) {
                stableOrderDateRef.current = { orderNumber: orderKey, date: String(Date.now()) };
            }

            const extras = buildExtras({
                user,
                userData,
                memberPoints,
                optimizelyCampaigns,
            });
            const next = buildPetsmartMedalliaContextFromOrder(purchase, extras, stableOrderDateRef.current.date);
            const nextSerialized = JSON.stringify(next);

            window.petsmartMedalliaContext = next;

            const isFirstPublish = lastPublishedRef.current === null;

            if (isFirstPublish) {
                // If the survey effect has already fired for this order (consent/flag became
                // enabled in a later render cycle), the SDK needs to be notified now because
                // the survey effect will not run again.  If the survey effect has *not* yet
                // fired it will do so in this same commit (declared second, runs after this
                // effect), so skip here to avoid a double call.
                if (surveyFiredForOrderRef.current === String(orderNumber)) {
                    window.KAMPYLE_ONSITE_SDK?.updatePageView?.();
                }
            } else if (nextSerialized !== lastPublishedRef.current) {
                // Enrichment update — context changed after the initial publish.
                window.KAMPYLE_ONSITE_SDK?.updatePageView?.();
            }

            lastPublishedRef.current = nextSerialized;
        } else {
            delete window.petsmartMedalliaContext;
            lastPublishedRef.current = null;
        }

        return () => {
            delete window.petsmartMedalliaContext;
        };
        // `purchase` omitted: enrichment only adds `webStyleId` (GTM), not Medallia fields.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderNumber, hasC0002, ffMedalliaCustom, user, userData, memberPoints, optimizelyCampaigns]);

    // Always fire updatePageView when the order arrives — flag/consent must not block surveys.
    // Declared SECOND so context (above) is already on window when the SDK is notified.
    // If the SDK is not yet loaded, registers a neb_OnsiteLoaded listener and retries —
    // matching the pattern used by useMedalliaForm and useMedalliaFormV2.
    useEffect(() => {
        if (!orderNumber) return;

        const fire = () => {
            if (window.KAMPYLE_ONSITE_SDK) {
                surveyFiredForOrderRef.current = String(orderNumber);
                window.KAMPYLE_ONSITE_SDK.updatePageView();
            }
        };

        if (window.KAMPYLE_ONSITE_SDK) {
            fire();
        } else {
            window.addEventListener('neb_OnsiteLoaded', fire, { once: true });
            return () => window.removeEventListener('neb_OnsiteLoaded', fire);
        }
    }, [orderNumber]);

    return null;
}
